import PropTypes from "prop-types";
import { Box, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
const tones = {
  activado: ["#16864b", "#f0faf4"],
  asignado: ["#1976b9", "#f2f8fd"],
  "con el supervisor": ["#7c3aed", "#f8f5ff"],
  registrado: ["#008b86", "#effaf9"],
  "sin asignar": ["#b96300", "#fff8f0"],
  "sin pistolear": ["#dc5900", "#fff5ee"],
};
export default function ConsoleStats({ stats, loading, error, total }) {
  const sum = stats.reduce((value, item) => value + Number(item.count_status || 0), 0);
  const ready = !loading && !error && sum === total;
  const most = stats.reduce(
    (best, item) => (Number(item.count_status) > Number(best?.count_status || 0) ? item : best),
    null
  );
  const format = (value) => Number(value).toLocaleString("es-CO");
  return (
    <Box
      component="section"
      aria-label="Estados de consola"
      aria-busy={loading}
      sx={{
        m: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ p: 2, bgcolor: "#f4f8f9" }}
      >
        <Typography variant="body2" fontWeight={700}>
          Estados de Consola
        </Typography>
        {ready && (
          <>
            <Chip size="small" label={`${stats.length} estados`} />
            <Chip size="small" color="info" label={`Total: ${format(total)}`} />
          </>
        )}
      </Stack>
      <Box sx={{ p: 2 }}>
        {loading ? (
          <>
            <Typography variant="caption" role="status">
              Actualizando estados con los filtros…
            </Typography>
            <Skeleton height={80} />
          </>
        ) : !ready ? (
          <Typography variant="body2">
            {error
              ? "No se pudieron actualizar los estados. Reintenta la consulta."
              : "El servidor no devolvió el resumen completo de estados."}
          </Typography>
        ) : !stats.length ? (
          <Typography variant="body2">No hay estados para los filtros seleccionados.</Typography>
        ) : (
          <>
            <Grid container spacing={1.5}>
              {stats.map((item) => {
                const [color, background] = tones[item.status_console.toLowerCase()] || [
                  "#0b5875",
                  "#f3f8fa",
                ];
                const percent = total ? (Number(item.count_status) / total) * 100 : 0;
                return (
                  <Grid item xs={12} sm={6} lg={4} key={item.status_console}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: background,
                        border: `1px solid ${color}30`,
                        borderRadius: 1.5,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" gap={1} mb={1.5}>
                        <Typography variant="body2" fontWeight={600}>
                          {item.status_console}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {format(item.count_status)}
                        </Typography>
                      </Stack>
                      <Box
                        role="progressbar"
                        aria-label={item.status_console}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={percent}
                        sx={{
                          height: 5,
                          borderRadius: 4,
                          overflow: "hidden",
                          bgcolor: `${color}20`,
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${percent}%`,
                            bgcolor: color,
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" display="block" textAlign="right" mt={0.5}>
                        {percent.toLocaleString("es-CO", {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        % del total
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              gap={1}
              sx={{ mt: 2, p: 1.5, bgcolor: "#f4f8f9", borderRadius: 1 }}
            >
              <Typography variant="caption">
                Estado más común: <strong>{most?.status_console || "—"}</strong>
              </Typography>
              <Typography variant="caption">
                Promedio por estado: <strong>{format(Math.round(total / stats.length))}</strong>
              </Typography>
              <Typography variant="caption">
                Total de estados: <strong>{stats.length}</strong>
              </Typography>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
ConsoleStats.propTypes = {
  stats: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  total: PropTypes.number.isRequired,
};
